import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@app/constants/queryKeys';
import { getContract, useAddress } from '@thirdweb-dev/react';
import { useConfig } from '@app/config';
import axios from 'axios';
import { useSupportedChain } from '@app/hooks/other/useSupportedChain';
import { Chain } from '@thirdweb-dev/chains';
import { metromAbi } from '@metrom-xyz/contracts/abi';
import { ApplicationConfig } from '@app/config/types';
import { formatUnits } from 'ethers/lib/utils';

export type MetromUserReward = {
  amount: string;
  campaignId: string;
  chainId: string;
  proof: string[];
  token: {
    address: string;
    decimals: string;
    name: string;
    symbol: string;
  };
};

async function fetcher(
  address: string | undefined,
  apiUrl: string | undefined,
  chainId: number | undefined,
  chain: Chain | undefined,
  config: ApplicationConfig | undefined
) {
  if (!address || !apiUrl || !chain || !config) {
    return;
  }

  const { data } = await axios.get<MetromUserReward[]>(
    `${apiUrl}/claims?chainId=${chainId}&amm=kim&user=${address}`
  );

  const result = await Promise.allSettled(
    data.map(async item => {
      const contract = await getContract({
        address: config?.METROM.DISTRIBUTOR,
        network: chain,
        contractTypeOrAbi: metromAbi
      });

      // @ts-ignore
      const rawClaimed = await contract.call('claimedCampaignReward', [
        item.campaignId,
        item.token.address,
        address
      ]);

      const rawRemaining = +item.amount - +rawClaimed.toString();

      const formattedRemaining = Number(
        formatUnits(rawRemaining, item.token.decimals)
      );

      if (formattedRemaining > 0) {
        return {
          data: item,
          isClaimed: false
        };
      } else {
        return {
          data: item,
          isClaimed: true
        };
      }
    })
  );

  return result
    .filter(
      item => item.status === 'fulfilled' && item.value.isClaimed === false
    )
    .map(
      item =>
        (
          item as PromiseFulfilledResult<{
            data: MetromUserReward;
            isClaimed: boolean;
          }>
        ).value.data
    );
}

export function useMetromUserRewards() {
  const address = useAddress();
  const config = useConfig();
  const chainId = useSupportedChain()?.chainId;
  const chain = useSupportedChain();

  return useQuery<MetromUserReward[] | undefined>(
    [QueryKeys.METROM_USER_REWARDS, address, config?.URLS.metromApiUrl],
    () => fetcher(address, config?.URLS.metromApiUrl, chainId, chain, config),
    {
      enabled: !!(address && chainId && config?.URLS.metromApiUrl)
    }
  );
}
